import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { authService } from '../services/authService';
import { useAuthStore } from '../store/authStore';
import { useToastStore } from '../store/toastStore';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { FormField } from '../components/ui/FormField';
import { AuthLayout } from '../components/layout/AuthLayout';
import { formatPhoneMask, unmaskPhone } from '../utils/phone';

interface FormState {
  name: string;
  email: string;
  phone: string;
  password: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  phone?: string;
  password?: string;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_COMPLETE_REGEX = /^\(\d{2}\) \d{4,5}-\d{4}$/;

function validateForm(form: FormState): FormErrors {
  const errors: FormErrors = {};

  if (!form.name.trim()) {
    errors.name = 'Nome é obrigatório';
  }

  if (!form.email.trim()) {
    errors.email = 'E-mail é obrigatório';
  } else if (!EMAIL_REGEX.test(form.email)) {
    errors.email = 'Informe um e-mail válido';
  }

  if (!form.phone.trim()) {
    errors.phone = 'Celular é obrigatório';
  } else if (!PHONE_COMPLETE_REGEX.test(form.phone)) {
    errors.phone = 'Informe um celular válido: (44) 99999-9999';
  }

  if (!form.password) {
    errors.password = 'Senha é obrigatória';
  } else if (form.password.length < 6) {
    errors.password = 'A senha deve ter pelo menos 6 caracteres';
  }

  return errors;
}

const BENEFITS = [
  { label: 'Roupinhas com carinho', description: 'Cada peça escolhida com amor e cuidado' },
  { label: 'Qualidade garantida', description: 'Tecidos macios e seguros para a pele do seu baby' },
  { label: 'Entrega rápida', description: 'Seu pedido chega com agilidade e segurança' },
];

export function RegisterPage() {
  const navigate = useNavigate();
  const login = useAuthStore((s) => s.login);
  const showToast = useToastStore((s) => s.show);

  const [form, setForm] = useState<FormState>({ name: '', email: '', phone: '', password: '' });
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Partial<Record<keyof FormState, boolean>>>({});
  const [loading, setLoading] = useState(false);

  const setField = (field: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    if (touched[field]) {
      setErrors(validateForm({ ...form, [field]: e.target.value }));
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const masked = formatPhoneMask(e.target.value);
    setForm((prev) => ({ ...prev, phone: masked }));
    if (touched.phone) {
      setErrors(validateForm({ ...form, phone: masked }));
    }
  };

  const handleBlur = (field: keyof FormState) => () => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    setErrors(validateForm(form));
  };

  const handleSubmit = async (e: React.SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();

    const allTouched: Partial<Record<keyof FormState, boolean>> = {
      name: true, email: true, phone: true, password: true,
    };
    setTouched(allTouched);

    const validationErrors = validateForm(form);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) return;

    setLoading(true);
    try {
      const data = await authService.register({ ...form, phone: unmaskPhone(form.phone) });
      login(data.accessToken, data.customerId, data.name, data.canManageStoreSettings);
      navigate('/');
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { errors?: string[] } } };
      showToast(axiosErr?.response?.data?.errors?.[0] ?? 'Não foi possível criar o acesso.', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      heading={<>Looks pensados<br /><span className="text-chocolate">com carinho</span><br />para quem você ama</>}
      description="Crie sua conta e acompanhe seus pedidos com facilidade, a qualquer hora e em qualquer lugar."
      benefits={BENEFITS}
    >
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-text-dark mb-1">Criar sua conta</h1>
        <p className="text-text-light text-sm">
          Preencha os dados abaixo para começar
        </p>
      </div>

      <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-5">
        <FormField label="Nome completo" required>
          <Input
            value={form.name}
            onChange={setField('name')}
            onBlur={handleBlur('name')}
            placeholder="Maria Silva"
            error={touched.name ? errors.name : undefined}
            autoComplete="name"
          />
        </FormField>

        <FormField label="E-mail" required>
          <Input
            type="email"
            value={form.email}
            onChange={setField('email')}
            onBlur={handleBlur('email')}
            placeholder="maria@email.com"
            error={touched.email ? errors.email : undefined}
            autoComplete="email"
          />
        </FormField>

        <FormField label="Celular" required>
          <Input
            type="tel"
            value={form.phone}
            onChange={handlePhoneChange}
            onBlur={handleBlur('phone')}
            placeholder="(44) 99999-9999"
            error={touched.phone ? errors.phone : undefined}
            autoComplete="tel"
          />
        </FormField>

        <FormField label="Senha" required>
          <Input
            type="password"
            value={form.password}
            onChange={setField('password')}
            onBlur={handleBlur('password')}
            placeholder="Mínimo 6 caracteres"
            error={touched.password ? errors.password : undefined}
            autoComplete="new-password"
          />
        </FormField>

        <Button type="submit" fullWidth loading={loading} size="lg" className="mt-1">
          Criar conta
        </Button>
      </form>

      <p className="text-center text-sm text-text-light mt-6">
        Já tem uma conta?{' '}
        <Link to="/login" className="text-chocolate font-medium hover:underline">
          Entrar
        </Link>
      </p>
    </AuthLayout>
  );
}
